import React, { useEffect, useState, useCallback } from 'react';
import { ChartBar } from '@phosphor-icons/react';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { getAssetAllocationReport, listAssetTeams, listProductClients } from '../api';
import { STATUS_BADGE, fmtLabel, fmtDate } from '../ui';

/**
 * Superadmin traceability view: every asset with its derived
 * Product → Client → Employee allocation chain.
 */
function AllocationReportPage() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [productFilter, setProductFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');

  useEffect(() => {
    listAssetTeams()
      .then((rows) => setProducts(rows.filter((r) => r.product_id)))
      .catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (!productFilter) {
      setClients([]);
      setClientFilter('');
      return;
    }
    listProductClients(productFilter).then(setClients).catch(() => setClients([]));
    setClientFilter('');
  }, [productFilter]);

  const refresh = useCallback(() => {
    setLoading(true);
    const params = {};
    if (productFilter) params.product_id = productFilter;
    if (clientFilter) params.client_id = clientFilter;
    getAssetAllocationReport(params)
      .then(setReport)
      .catch((e) => console.error('Failed to fetch allocation report:', e))
      .finally(() => setLoading(false));
  }, [productFilter, clientFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div data-testid="allocation-report-page">
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
          className="input-field" style={{ padding: '6px 8px', fontSize: '12px' }}>
          <option value="">All Products</option>
          {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
        </select>
        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
          className="input-field" style={{ padding: '6px 8px', fontSize: '12px' }} disabled={!productFilter}>
          <option value="">All Clients</option>
          {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <SkeletonBlock />
      ) : report.length === 0 ? (
        <EmptyState icon={ChartBar} title="No assets found" description="Adjust the filters, or add assets in Inventory." />
      ) : (
        <div>
          <div style={{ marginBottom: '6px', fontSize: '12px', color: '#6B7280' }}>
            Showing {report.length} asset{report.length === 1 ? '' : 's'}
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Asset</th><th>Type</th><th>Status</th><th>Assigned Employee</th>
                  <th>Client</th><th>Product</th><th>Created</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r) => (
                  <tr key={r.asset_id}>
                    <td style={{ fontWeight: '600' }}>{r.asset_tag || r.asset_id} — {r.name}</td>
                    <td style={{ textTransform: 'capitalize' }}>{fmtLabel(r.asset_type)}</td>
                    <td>
                      <span className="badge" style={{ background: STATUS_BADGE[r.status] || '#F3F4F6', textTransform: 'capitalize' }}>
                        {fmtLabel(r.status)}
                      </span>
                    </td>
                    <td>{r.assigned_to_name || '—'}</td>
                    <td>{r.client_name || '—'}</td>
                    <td>{r.product_name || '—'}</td>
                    <td>{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllocationReportPage;
